<template>
  <div v-if="volunteer" class="container volunteerfindnetmatch">
    <div class="row header">
      <div class="col-lg-4 col-xl-4">
        <HomeButton v-if="group" :group-id="group.id" />
        <BackButton />
      </div>
      <div v-if="customerSelected" class="col-lg-8 col-xl-9 d-none d-lg-block">
        <h4>{{ $t('customerSearch.volunteerMatchTitle') }}</h4>
      </div>
    </div>

    <div v-if="group && volunteer" class="row content">
      <div class="col-lg-4 col-xl-3 mb-3 mb-lg-0 volunteer-area">
        <VolunteerCardNet
          v-model="volunteer"
          :group="group"
          @refresh="refresh"
        />
        <div v-show="isSending" class="loading">
          <div class="spinner">
            <loading />
          </div>
        </div>
      </div>

      <div class="col-lg-8 col-xl-9 customer-area">
        <div v-if="!customerSelected" class="row customer-selection">
          <div class="col-md-4 col-lg-4 order-md-2 search-filter">
            <!--<matchfilter v-model="matchFilter" :is-net="group.netFriendship" @filterchanged="onFilterChanged" />-->
            <customerFilter
              :group="group"
              :filter-type="'CUSTOMER'"
              :show-all="false"
              @filterchanged="onFilterChanged"
            />
          </div>

          <div class="col-md-8 col-lg-8 order-md-1 customer-list">
            <h4>{{ $t('customerSearch.volunteerMatchTitle') }}</h4>
            <volunteermatchresults
              :list="filteredcustomers"
              @customerSelected="onCustomerSelected"
            />
            <div v-show="isLoading" class="loading">
              <div class="spinner">
                <loading />
              </div>
            </div>
            <div v-if="!isLoading && noResults" class="noresults infocontainer">
              <div class="info warning">
                <font-awesome-icon icon="exclamation-triangle" />{{
                  $t('customerSearch.noResults')
                }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="customerSelected" class="row d-lg-none customer-heading">
          <h4>{{ $t('customerSearch.volunteerMatchTitle') }}</h4>
        </div>
        <div v-if="customerSelected" class="row ml-lg-1 customer-summary">
          <div class="col">
            <CustomerSummaryNet
              :volunteer="volunteer"
              :customer="customer"
              :group="group"
              :score="score"
              :is-first="isFirst"
              :is-last="isLast"
              :is-inappropriate="isInappropriate"
              @prev="onPrev"
              @next="onNext"
              @close="onClose"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import * as _ from 'lodash'
import VolunteerCardNet from './VolunteerCardNet.vue'
import CustomerSummaryNet from './CustomerSummaryNet.vue'
import volunteermatchresults from './VolunteerMatchResults.vue'
import BackButton from '../BackButton.vue'
import HomeButton from '../HomeButton.vue'
import loading from '../SPRLoading.vue'
import datehelper from '@/helpers/datehelper'
import friendMatch from '@/mixins/friendMatch'
import customerFilter from './CustomerFilter.vue'

import bitmaskhelper from '@/helpers/bitmaskhelper'

import {
  MatchItemNetCustomerDto,
  FriendCustomerNetDto,
  UserProfileDto,
  FriendManagementDto,
  CustomerState,
  GetFriendProfileByFriendVolunteerIdCriteria,
  FriendshipNetCommandCriteria
} from '@/types/index'
import { MatchFilter, NetMatchPair } from '@/types/custom'

// the data object
export interface TemplateComponentData {
  customerlist: Array<MatchItemNetCustomerDto>
  filteredcustomers: Array<MatchItemNetCustomerDto>
  customer: undefined | FriendCustomerNetDto
  volunteer: undefined | UserProfileDto
  score: number
  isLoading: boolean
  isSending: boolean
  matchFilter: MatchFilter
}

export default Vue.extend({
  name: 'VolunteerFindNetMatch',
  components: {
    VolunteerCardNet,
    customerFilter,
    CustomerSummaryNet,
    volunteermatchresults,
    BackButton,
    HomeButton,
    loading
  },
  mixins: [friendMatch],
  props: {
    group: {
      type: Object as () => FriendManagementDto,
      required: true
    },
    volunteerid: {
      type: Number,
      required: true
    }
  },
  data(): TemplateComponentData {
    return {
      customerlist: [] as Array<MatchItemNetCustomerDto>,
      filteredcustomers: [] as Array<MatchItemNetCustomerDto>,
      customer: undefined,
      volunteer: undefined,
      score: 0,
      isLoading: false,
      isSending: false,
      matchFilter: {
        customerTypes: [],
        languages: [],
        motherTongs: [],
        ages: [],
        hasGroupMembership: false,
        genders: []
      }
    }
  },
  computed: {
    isInactive(): boolean {
      return this.group?.netFriendship
        ? this.volunteer?.friendInfo.netProfile.state ===
            CustomerState.NonActive
        : this.volunteer?.friendInfo.standardProfile.state ===
            CustomerState.NonActive
    },
    noResults(): boolean {
      return this.customerlist === null || this.filteredcustomers.length === 0
    },
    isFirst(): boolean {
      if (this.filteredcustomers.length <= 1) return true
      return (
        this.customer?.id ===
        this.filteredcustomers[0].match.customerProfileNetId
      )
    },
    isLast(): boolean {
      if (this.filteredcustomers.length <= 1) return true
      return (
        this.customer?.id ===
        this.filteredcustomers[this.filteredcustomers.length - 1].match
          .customerProfileNetId
      )
    },
    isInappropriate(): boolean {
      if (!this.customerSelected) return false

      var selectedCustomer = this.filteredcustomers.find(
        (x) => x.match.customerProfileNetId === this.customer?.id
      )
      return selectedCustomer ? selectedCustomer.isInappropriateMatch : false
    },
    hasFriendship(): boolean {
      return (
        this.volunteer?.friendInfo.netProfile?.friendships != null &&
        this.volunteer.friendInfo.netProfile.friendships.length > 0
      )
    },
    customerSelected(): boolean {
      return !_.isEmpty(this.customer)
    }
  },
  mounted(): void {
    this.matchFilter = {
      customerTypes: [],
      languages: [],
      motherTongs: [],
      ages: [],
      hasGroupMembership: false,
      genders: []
    }
    this.getVolunteer()
    this.getCustomers()
    window.scrollTo(0, 0)

    this.$bus.$on('inappropriate-friendship', this.onInappropriateFriendship)
    this.$bus.$on(
      'end-inappropriate-friendship',
      this.onEndInappropriateFriendship
    )
    this.$bus.$on('initiate-friendship', this.onInitiate)
  },
  beforeDestroy(): void {
    this.$bus.$off('inappropriate-friendship')
    this.$bus.$off('end-inappropriate-friendship')
    this.$bus.$off('initiate-friendship')
  },
  methods: {
    getVolunteer(): void {
      var self = this
      var criteria: GetFriendProfileByFriendVolunteerIdCriteria = {
        friendManagementGroupId: this.group.id,
        friendVolunteerId: this.volunteerid,
        includeFriendships: true,
        includeFriendPools: false
      }
      var volunteerUrl = `${this.$store.state.apiBases.friendservice}${this.$store.state.apiEndpoints.volunteergetnetprofilebyid[0]}`
      this.$http.post(volunteerUrl, criteria).then((resp) => {
        self.volunteer = resp.data
      })
    },
    getCustomer(customerId: number): void {
      var self = this
      var getCustomerUrl = `${
        this.$store.state.apiBases.friendservice +
        this.$store.state.apiEndpoints.customergetnetbyid[0]
      }`
        .replace('{customerid}', customerId.toString())
        .replace('{groupid}', this.group.id.toString())
      this.isLoading = true
      this.$http.get(getCustomerUrl).then(
        (resp) => {
          if (resp.data) {
            self.customer = resp.data
            self.isLoading = false
          } else {
            this.displayError(this.$t('common.fetchFailed'))
          }
        },
        (f) => this.displayError(f)
      )
    },
    getCustomers(): void {
      var self = this
      this.isLoading = true
      var customersUrl = `${this.$store.state.apiBases.friendservice}${this.$store.state.apiEndpoints.volunteerfindnetmatch[0]}`

      customersUrl = customersUrl
        .replace('{groupid}', this.group.id.toString())
        .replace('{volunteerid}', this.volunteerid.toString())
      this.$http.get(customersUrl).then((resp) => {
        self.customerlist = _.reverse(_.sortBy(resp.data.items, ['score']))
        self.filteredcustomers = self.customerlist
        self.isLoading = false
      })
    },
    onCustomerSelected(match: MatchItemNetCustomerDto): void {
      window.scrollTo(0, 0)
      this.getCustomer(match.match.customerProfileNetId)
      this.score = match.score
    },
    onInitiate(activation: NetMatchPair): void {
      var data: FriendshipNetCommandCriteria = {
        customerProfileNetId: activation.customer.id,
        volunteerNetProfileId: activation.volunteer.friendInfo.netProfile.id,
        groupId: this.group.id
      }
      var url = `${this.$store.state.apiBases.friendservice}${this.$store.state.apiEndpoints.friendshipinitiatenet[0]}`
      this.$http.post(url, data).then(
        (resp) => {
          this.$router.push(
            `/friend/volunteermatch/${this.group.id}/${this.volunteerid}/${activation.customer.id}`
          )
        },
        (f) => {
          this.displayError(f || this.$t('common.fetchFailed'))
        }
      )
    },
    onPrev(): void {
      var self = this
      var currentCustomer = _.findIndex(
        self.filteredcustomers,
        function (item) {
          return item.match.customerProfileNetId === self.customer?.id
        }
      )

      if (currentCustomer > 0) {
        var id =
          this.filteredcustomers[currentCustomer - 1].match.customerProfileNetId
        this.getCustomer(id)
        self.score = self.filteredcustomers[currentCustomer - 1].score
      }
    },
    onNext(): void {
      var self = this
      var currentCustomer = _.findIndex(
        self.filteredcustomers,
        function (item) {
          return item.match.customerProfileNetId === self.customer?.id
        }
      )

      if (
        currentCustomer > -1 &&
        currentCustomer < self.filteredcustomers.length - 1
      ) {
        var id =
          self.filteredcustomers[currentCustomer + 1].match.customerProfileNetId
        this.getCustomer(id)
        self.score = self.filteredcustomers[currentCustomer + 1].score
      }
    },
    onClose(): void {
      this.customer = undefined
    },
    onInappropriateFriendship(friendshipInfo: NetMatchPair): void {
      var data: FriendshipNetCommandCriteria = {
        customerProfileNetId: friendshipInfo.customer.id,
        volunteerNetProfileId:
          friendshipInfo.volunteer.friendInfo.netProfile.id,
        groupId: this.group.id
      }
      var url = `${
        this.$store.state.apiBases.friendservice +
        this.$store.state.apiEndpoints.friendshipmarkinappropriatenet[0]
      }`
      this.$http.post(url, data).then(
        (resp) => {
          this.customer = undefined
          this.getCustomers()
        },
        (f) => {
          // @ts-ignore (Mixins not found)
          this.showAlertError(this.$t('common.fetchFailed'))
        }
      )
    },
    onEndInappropriateFriendship(friendshipInfo: NetMatchPair): void {
      var data: FriendshipNetCommandCriteria = {
        customerProfileNetId: friendshipInfo.customer.id,
        volunteerNetProfileId:
          friendshipInfo.volunteer.friendInfo.netProfile.id,
        groupId: this.group.id
      }
      var url = `${
        this.$store.state.apiBases.friendservice +
        this.$store.state.apiEndpoints.friendshipendinappropriatenet[0]
      }`
      this.isLoading = true
      this.$http.post(url, data).then(
        (resp) => {
          this.customer = undefined
          this.getCustomers()
        },
        (f) => {
          // @ts-ignore (Mixins not found)
          this.showAlertError(this.$t('common.fetchFailed'))
        }
      )
    },
    onFilterChanged(val): void {
      var self = this
      this.matchFilter = val
      this.filteredcustomers = this.customerlist.filter(function (
        customerObject
      ) {
        return (
          (!self.matchFilter.customerTypes?.length ||
            bitmaskhelper
              .bitmaskToArray(customerObject.match.customerTypes)
              .some((customerType) =>
                self.matchFilter.customerTypes?.includes(customerType)
              )) &&
          (!self.matchFilter.languages?.length ||
            customerObject.match.spokenLanguages.some((customerLanguage) =>
              self.matchFilter.languages?.includes(customerLanguage)
            )) &&
          (!self.matchFilter.motherTongs?.length ||
            self.matchFilter.motherTongs?.includes(
              customerObject.match.nativeLanguage
            )) &&
          (!self.matchFilter.ages?.length ||
            self.matchFilter.ages?.includes(
              friendMatch.methods.calculateBitmaskFromAge(
                datehelper.calculateAge(customerObject.match.birthDate)
              )
            ))
        )
      })
    },
    refresh(): void {
      window.location.reload()
    },
    displayError(err: Response | any): void {
      if (Array.isArray(err.body)) {
        err.body.forEach((x) => {
          // @ts-ignore
          this.showAlertError(x.errorMessage)
        })
      } else if (err.body && err.body.errorMessage) {
        // @ts-ignore
        this.showAlertError(err.errorMessage)
      } else if (err.bodyText) {
        // @ts-ignore
        this.showAlertError(err.bodyText)
      } else {
        // @ts-ignore
        this.showAlertError(this.$t('common.fetchFailed'))
      }
    }
  }
})
</script>

<style lang="scss">
@import '../../assets/scss/_variables.scss';

.volunteerfindnetmatch {
  .header {
    margin-top: 10px;
    margin-bottom: 15px;
    .nav-header {
      border-bottom: 0;
    }
    h4 {
      padding-left: 10px;
    }
  }
  .content {
    .volunteer-area {
      padding-top: 30px;
      background-color: $brand-white;
      border-left: solid 5px $volunteer-color-strong;
    }

    .customer-area {
      .customer-selection {
        height: 100%;
        .search-filter {
          background-color: $brand-white;
          padding-top: 20px;
        }
        .customer-list {
          h4 {
            margin: 20px 0 10px 0;
          }
          .loading {
            position: absolute;
            display: flex;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            height: 100%;
            width: 100%;
            background-color: rgba(255, 255, 255, 0.8);
            .spinner {
              margin: auto;
              color: $brand-red;
            }
          }
          .infocontainer {
            height: 200px;
            width: 100%;
            background-color: $brand-white;
            display: flex;
            justify-content: center;
            align-items: center;
            align-content: center;
            padding: 50px;
            .info {
              margin: auto;
              svg {
                margin-right: 5px;
                color: $brand-red;
                &.warning {
                  color: $brand-warning;
                }
              }
            }
          }
        }
      }
      .customer-heading {
        h4 {
          padding-left: 10px;
        }
      }
      .customer-summary {
        @media (min-width: $media-lg) {
          height: 100%;
        }
      }
    }
  }
}
</style>
